import React from 'react';
import { Form, Input, Row, Col, Select } from 'antd';

const { TextArea } = Input;

/**
 * Firm-basics Form.Item fields, shared between AdvisoryFirmOnboarding's step 1 and
 * AdvisoryFirmSettings' edit form. Must be rendered inside an antd <Form> — field names match
 * `AdvisoryFirm` Prisma columns 1:1 (see .claude/design/la-onboarding-razorpay.md), except
 * `founderSummary` (see comment on that field below).
 *
 * Country defaults to India per Commandment XII — the parent form's `initialValues` sets
 * `country: 'India'` for a brand-new firm; this component does not hardcode a default itself
 * so an existing firm's saved country is never silently overwritten.
 */
const AdvisoryFirmBasicsFields = () => (
    <>
        <Form.Item
            name="firmName"
            label="Firm name"
            rules={[{ required: true, message: 'Firm name is required' }]}
        >
            <Input placeholder="e.g. Aurigo Advisory Partners" size="large" />
        </Form.Item>

        <Row gutter={16}>
            <Col span={12}>
                <Form.Item name="legalName" label="Legal name">
                    <Input placeholder="Registered legal entity name" />
                </Form.Item>
            </Col>
            <Col span={12}>
                <Form.Item name="website" label="Website">
                    <Input placeholder="https://…" />
                </Form.Item>
            </Col>
        </Row>

        <Row gutter={16}>
            <Col span={12}>
                <Form.Item
                    name="gstin"
                    label="GSTIN"
                    rules={[{ pattern: /^[0-9A-Z]{15}$/, message: '15-character GSTIN', warningOnly: true }]}
                >
                    <Input placeholder="Indian GST registration number" />
                </Form.Item>
            </Col>
            <Col span={12}>
                <Form.Item
                    name="pan"
                    label="PAN"
                    rules={[{ pattern: /^[A-Z]{5}[0-9]{4}[A-Z]$/, message: '10-character PAN', warningOnly: true }]}
                >
                    <Input placeholder="Permanent Account Number" style={{ textTransform: 'uppercase' }} />
                </Form.Item>
            </Col>
        </Row>

        <Form.Item name="linkedInUrl" label="LinkedIn URL" rules={[{ type: 'url', message: 'Enter a valid URL' }]}>
            <Input placeholder="https://www.linkedin.com/company/…" />
        </Form.Item>

        <Form.Item name="address" label="Address">
            <TextArea rows={2} placeholder="Street address" maxLength={500} showCount />
        </Form.Item>

        <Row gutter={16}>
            <Col span={8}>
                <Form.Item name="city" label="City">
                    <Input />
                </Form.Item>
            </Col>
            <Col span={8}>
                <Form.Item name="state" label="State">
                    <Input />
                </Form.Item>
            </Col>
            <Col span={8}>
                <Form.Item name="pincode" label="Pincode">
                    <Input />
                </Form.Item>
            </Col>
        </Row>

        <Form.Item name="country" label="Country">
            <Select
                showSearch
                options={[
                    { value: 'India', label: 'India' },
                    { value: 'United States', label: 'United States' },
                    { value: 'United Kingdom', label: 'United Kingdom' },
                    { value: 'Singapore', label: 'Singapore' },
                    { value: 'United Arab Emirates', label: 'United Arab Emirates' },
                    { value: 'Other', label: 'Other' },
                ]}
            />
        </Form.Item>

        <Form.Item name="aboutFirm" label="About the firm">
            <TextArea rows={4} placeholder="A short description clients will see" maxLength={2000} showCount />
        </Form.Item>

        {/*
          `founderProfileJson` is a JSON column on the backend but has no documented internal
          shape yet (the spec marks it "AI-enriched" without a field list). Rendered here as a
          single free-text summary; the page components wrap it to/from
          `JSON.stringify({ summary })` on submit/load rather than exposing raw JSON to the LA.
          This is an explicit assumption pending backend confirmation — see handoff notes.
        */}
        <Form.Item name="founderSummary" label="Founder / leadership profile (optional)">
            <TextArea rows={3} placeholder="A short bio of the firm's founder(s) / leadership" maxLength={1500} showCount />
        </Form.Item>
    </>
);

export default AdvisoryFirmBasicsFields;
